import { LeftOutlined } from '@ant-design/icons'
import { Button, Input, Modal, Select, Table } from 'antd'
import { AxiosError } from 'axios'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import { useAtomModal } from '@/components/Atoms/AtomModal/useAtomModal'
import AtomSwitch from '@/components/Atoms/AtomSwitch/AtomSwitch'
import AtomTable from '@/components/Atoms/AtomTable/AtomTable'
import Loader from '@/components/Atoms/Loader/Loader'
import ValidationErrorModal from '@/components/Cart/ValidationErrorModal/ValidationErrorModal'
import { OrderExportButtons } from '@/components/Orders/OrderExport/OrderExportButtons/OrderExportButtons'
import {
  DEFAULT_REGION_BANK_HEADER_VALUE,
  DELAY,
  ORDER_PRODUCT_STATUSES,
  ORDER_STATUSES,
} from '@/constants'
import { createSafeRowMouseUp } from '@/hooks/ui/createSafeRowMouseUp'
import OrderProductCard from '@/pages/Profile/Orders/Order/OrderProductCard/OrderProductCard'
import OrderCard from '@/pages/Profile/Orders/OrderCard/OrderCard'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'
import { useOrderByCodeStore } from '@/stores/cart/orderByCodeStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  ApiErrorResponseData,
  OrderInputDataInterface,
  OrderInterface,
  OrderPermissionsInterface,
  OrderProductModel,
  OrderShortType,
  OrderStatusType,
  SubOrderRequirementInterface,
  TakeInWorkPreviewResponseInterface,
  ValidationErrorResponse,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { getOfferValidationError } from '@/utils/api/errors/offerValidationError'
import { isSupplierStaff } from '@/utils/auth/userRoles'
import { trimTrailingZeros } from '@/utils/format/trimTrailingZeros'
import { withVat } from '@/utils/price/vat'
import getProductKey from '@/utils/products/getProductKey'
import { toFlatOfferLines } from '@/utils/products/offerLines'

import OrderProductsSelectionModal from '../OrderProductsSelectionModal/OrderProductsSelectionModal'
import { getOrderTotals } from './lib/orderTotals'
import {
  formatProductsFromServer,
  getColumns as getProductsColumns,
  ORDER_PRODUCT_COLUMN_WIDTHS,
  orderToSupplierColumns,
} from './Order.config'
import cn from './Order.module.scss'
import OrderInfoCards from './OrderInfoCards/OrderInfoCards'

type OrderTab = 'products' | 'suppliers'

const ORDER_TAB_OPTIONS = [
  { value: 'products' as const, label: 'Товары' },
  { value: 'suppliers' as const, label: 'Заказы поставщикам' },
]

const getFieldLabel = (type: string): string => {
  const labels: Record<string, string> = {
    CONTRACT: 'Договор',
    DESTINATION_STORE: 'Склад назначения',
    CONSTRUCTION_SITE: 'Объект',
  }
  return labels[type] || type
}

const getFieldPlaceholder = (type: string): string => {
  const placeholders: Record<string, string> = {
    CONTRACT: 'договор',
    DESTINATION_STORE: 'склад',
    CONSTRUCTION_SITE: 'объект',
  }
  return placeholders[type] || type.toLowerCase()
}

function Order() {
  const [order, setOrder] = useState<OrderInterface>({} as OrderInterface)
  const [orderProducts, setOrderProducts] = useState<OrderProductModel[]>([])
  const [ordersToSuppliers, setOrdersToSuppliers] = useState<OrderShortType[]>(
    []
  )
  const [ordersToSuppliersLoaded, setOrdersToSuppliersLoaded] = useState(false)

  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false)
  const [currentTab, setCurrentTab] = useState<OrderTab>('products')
  const [loading, setLoading] = useState(false)
  const [isInOrderToSupplier, setInOrderToSupplier] = useState(false)
  const [showTakeInWorkConfirmModal, setShowTakeInWorkConfirmModal] =
    useState(false)
  const [, setRefreshOrdersToSuppliers] = useState(0)

  const [orderPermissions, setOrderPermissions] =
    useState<OrderPermissionsInterface | null>(null)

  const [subOrderModalVisible, setSubOrderModalVisible] = useState(false)
  const [previewData, setPreviewData] =
    useState<TakeInWorkPreviewResponseInterface | null>(null)
  const [subOrderComments, setSubOrderComments] = useState<{
    [supplierOwnerOrgId: string]: string
  }>({})
  const [subOrderInputs, setSubOrderInputs] = useState<{
    [key: string]: OrderInputDataInterface
  }>({})

  const [isLastProductDeleteModalOpen, setIsLastProductDeleteModalOpen] =
    useState(false)
  const [productPendingDeletion, setProductPendingDeletion] =
    useState<OrderProductModel | null>(null)

  const { userId, selectedOrganisation } = useUserInfoStore((state) => state)
  const isStaff = useUserInfoStore((state) => isSupplierStaff(state.tokenRoles))

  const duplicateOrderProductsQuantities = useOrderByCodeStore(
    (state) => state.duplicateOrderProductsQuantities
  )
  const setDuplicateOrderProductsQuantities = useOrderByCodeStore(
    (state) => state.setDuplicateOrderProductsQuantities
  )

  const { code } = useParams()
  const [modal, contextHolder] = Modal.useModal()
  const [atomModal, atomModalHolder] = useAtomModal()
  const [validationError, setValidationError] =
    useState<ValidationErrorResponse | null>(null)

  const showOrderError = (error: unknown) => {
    const offerError = getOfferValidationError(error)
    if (offerError) {
      setValidationError(offerError)
    } else {
      handleApiError(error, modal)
    }
  }
  const navigate = useNavigate()
  const screen = useContext(ScreenSizeContext)

  const isOrganisationSelected =
    Object.keys(selectedOrganisation || {}).length > 0

  const handleOpenSelection = () => {
    setIsSelectionModalOpen(true)
  }

  const fetchOrderData = useCallback(async () => {
    if (!code || code === 'undefined' || code === 'null') return

    try {
      const [orderResponse, permissionsResponse] = await Promise.all([
        api.get(`/orders/${code}`),
        api.get(`/orders/${code}/permissions`),
      ])

      const orderData: OrderInterface =
        orderResponse.data.details || orderResponse.data
      const orderModel = orderData.orderModel

      if (localStorage.getItem('userUnviewedOrders')) {
        const userUnviewedOrders = JSON.parse(
          String(localStorage.getItem('userUnviewedOrders'))
        )
        let newUserUnviwedOrders = []
        if (userId) {
          newUserUnviwedOrders = userUnviewedOrders[userId].filter(
            (orderCode: string) => orderCode != String(orderModel.code)
          )
        }
        userUnviewedOrders[userId] = newUserUnviwedOrders
        localStorage.setItem(
          'userUnviewedOrders',
          JSON.stringify(userUnviewedOrders)
        )
      }

      if (
        !orderModel.sourceOrderShortModel ||
        !Object.keys(orderModel.sourceOrderShortModel).length
      ) {
        setInOrderToSupplier(false)
        setOrdersToSuppliersLoaded(false)
      }
      if (orderModel.sourceOrderShortModel) {
        setInOrderToSupplier(true)
        setCurrentTab('products')
      }

      setOrder(orderData)

      const permissions =
        permissionsResponse.data.permissions || permissionsResponse.data
      setOrderPermissions(permissions)

      const prods = formatProductsFromServer(orderData.products)
      const duplicateQuantities: { [x: string]: number } = {}
      prods.forEach((prod) => {
        const key = String(getProductKey(prod))
        duplicateQuantities[key] = prod.quantity
      })
      setDuplicateOrderProductsQuantities(duplicateQuantities)
      setOrderProducts([...prods])
    } catch (error) {
      console.error('Error loading order', error)
      toast.error('Не удалось загрузить заказ')
      navigate('/profile')
    }
  }, [code, userId])

  useEffect(() => {
    setLoading(true)
    fetchOrderData().finally(() => {
      setTimeout(() => setLoading(false), DELAY)
    })
  }, [fetchOrderData])

  useEffect(() => {
    if (
      code &&
      !isInOrderToSupplier &&
      currentTab === 'suppliers' &&
      !ordersToSuppliersLoaded
    ) {
      setLoading(true)
      api
        .get(`/orders/${code}/to-suppliers`)
        .then((response) => {
          setOrdersToSuppliers(response.data.orders || response.data)
          setOrdersToSuppliersLoaded(true)
        })
        .catch((error) => {
          handleApiError(error, modal)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [code, isInOrderToSupplier, currentTab, ordersToSuppliersLoaded])

  const handleTakeInWork = async () => {
    if (!orderPermissions) return

    if (!orderPermissions.requiresSubOrderInputs) {
      await executeTakeInWork({ createSubOrders: false })
      return
    }

    setShowTakeInWorkConfirmModal(true)
  }

  const fetchTakeInWorkPreview = async () => {
    const loadingToast = toast.loading('Подготовка данных...', {
      description: `Заказ №${code}`,
    })

    try {
      setLoading(true)
      const response = await api.get(
        `/orders/${code}/actions/take-in-work/preview`,
        {
          headers: {
            'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE,
          },
        }
      )

      const preview = response.data.preview || response.data
      setPreviewData(preview)

      const initialInputs: { [key: string]: OrderInputDataInterface } = {}

      if (
        preview.subOrderRequirements &&
        Array.isArray(preview.subOrderRequirements)
      ) {
        preview.subOrderRequirements.forEach(
          (req: SubOrderRequirementInterface) => {
            if (req.requiredInputs && Array.isArray(req.requiredInputs)) {
              req.requiredInputs.forEach((input) => {
                const key = `${req.supplierOwnerOrgId}_${input.type}`
                initialInputs[key] = {
                  supplierOwnerOrgId: req.supplierOwnerOrgId,
                  type: input.type,
                  value: '',
                }
              })
            }
          }
        )
      }

      setSubOrderInputs(initialInputs)
      setSubOrderComments({})
      toast.dismiss(loadingToast)
      setLoading(false)
      setSubOrderModalVisible(true)
    } catch (error) {
      toast.dismiss(loadingToast)
      setLoading(false)
      showOrderError(error)
    }
  }

  const executeTakeInWork = async (request: {
    createSubOrders: boolean
    subOrderInputs?: OrderInputDataInterface[]
  }) => {
    const loadingToast = toast.loading(
      request.createSubOrders
        ? 'Создание под-заказов...'
        : 'Взятие в работу...',
      {
        description: `Заказ №${code}`,
      }
    )

    try {
      setLoading(true)
      await api.post(`/orders/${code}/actions/take-in-work`, request, {
        headers: {
          'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE,
        },
      })

      if (localStorage.getItem('userStoredOrdersStatuses')) {
        const userStoredOrdersStatuses = JSON.parse(
          String(localStorage.getItem('userStoredOrdersStatuses'))
        )
        userStoredOrdersStatuses[userId][order.orderModel.code] = 'IN_WORK'
        localStorage.setItem(
          'userStoredOrdersStatuses',
          JSON.stringify(userStoredOrdersStatuses)
        )
      }

      if (request.createSubOrders) {
        setRefreshOrdersToSuppliers((prev) => prev + 1)
      }

      setSubOrderModalVisible(false)
      setShowTakeInWorkConfirmModal(false)

      await fetchOrderData()

      toast.success(
        request.createSubOrders
          ? 'Заказ взят в работу, под-заказы созданы'
          : 'Заказ взят в работу',
        {
          id: loadingToast,
          description: `Заказ №${code}`,
        }
      )
    } catch (error) {
      toast.dismiss(loadingToast)
      const responseData =
        error instanceof AxiosError
          ? (error.response?.data as ApiErrorResponseData | undefined)
          : undefined
      const message: string = responseData?.message || ''
      const isAlreadyInWork =
        message.includes('IN_WORK') && message.includes('не разрешен')
      if (isAlreadyInWork) {
        toast.info('Заказ уже взят в работу, обновляем данные...', {
          description: `Заказ №${code}`,
        })
        await fetchOrderData()
      } else {
        showOrderError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleStatusTransition = async (status: OrderStatusType) => {
    if (!code) return

    const statusTitle =
      ORDER_PRODUCT_STATUSES[status] || ORDER_STATUSES[status] || status

    const loadingToast = toast.loading('Обновление заказа...', {
      description: `Перевод в статус "${statusTitle}"`,
    })

    try {
      setLoading(true)
      await api.patch(
        `/orders/${code}/status`,
        { status: status },
        {
          headers: {
            'Region-Bank': DEFAULT_REGION_BANK_HEADER_VALUE,
          },
        }
      )

      if (localStorage.getItem('userStoredOrdersStatuses')) {
        const userStoredOrdersStatuses = JSON.parse(
          String(localStorage.getItem('userStoredOrdersStatuses'))
        )
        if (userId) {
          if (!userStoredOrdersStatuses[userId])
            userStoredOrdersStatuses[userId] = {}
          userStoredOrdersStatuses[userId][order.orderModel.code] = status
          localStorage.setItem(
            'userStoredOrdersStatuses',
            JSON.stringify(userStoredOrdersStatuses)
          )
        }
      }

      await fetchOrderData()

      toast.success('Статус изменен', {
        id: loadingToast,
        description: `${statusTitle} — Заказ №${code}`,
      })
    } catch (error) {
      toast.dismiss(loadingToast)
      handleApiError(error, modal)
    } finally {
      setLoading(false)
    }
  }

  const accentButtonClassName = `${cn.accentButton} ${isOrganisationSelected ? cn.accentButtonBlue : ''}`

  const handleSubOrderFormSubmit = () => {
    const allInputs = Object.values(subOrderInputs)
    const hasEmptyFields = allInputs.some((input) => !input.value)

    if (hasEmptyFields) {
      toast.warning('Заполните все поля', {
        description: 'Все обязательные поля должны быть заполнены',
      })
      return
    }

    const inputsWithComments = allInputs.map((input) => ({
      ...input,
      comment: subOrderComments[input.supplierOwnerOrgId] || undefined,
    }))

    executeTakeInWork({
      createSubOrders: true,
      subOrderInputs: inputsWithComments,
    })
  }

  const handleInputChange = (
    supplierOwnerOrgId: string,
    type: string,
    value: string
  ) => {
    const key = `${supplierOwnerOrgId}_${type}`
    setSubOrderInputs((prev) => ({
      ...prev,
      [key]: {
        supplierOwnerOrgId,
        type: type as 'CONTRACT' | 'DESTINATION_STORE' | 'CONSTRUCTION_SITE',
        value,
      },
    }))
  }

  const executeDeleteProduct = (
    product: OrderProductModel,
    isLastItem: boolean = false
  ) => {
    const productKey = String(getProductKey(product))
    const productName = product.nomenclatureDto?.description || 'Товар'

    const loadingToast = toast.loading('Удаление товара...', {
      description: productName,
    })

    setLoading(true)

    api
      .delete(`/orders/${order.orderModel.code}/product-item`, {
        headers: {
          'Region-bank': DEFAULT_REGION_BANK_HEADER_VALUE,
        },
        data: {
          key: {
            supplierId: product.storeShortModel?.supplierId,
            productCode: product.nomenclatureDto.code,
            storeCode: product.storeShortModel?.code,
          },
          route: product.route,
        },
      })
      .then(() => {
        if (isLastItem) {
          toast.success('Товар удален, заказ удален', {
            id: loadingToast,
            description: `Заказ №${code}`,
          })
          setIsLastProductDeleteModalOpen(false)
          setTimeout(() => {
            navigate('/profile')
          }, 1000)
        } else {
          toast.success('Товар удален', {
            id: loadingToast,
            description: productName,
          })
          const updatedOrderProducts = orderProducts.filter(
            (p) => getProductKey(p) !== productKey
          )
          setOrderProducts(updatedOrderProducts)
          setLoading(false)
        }
      })
      .catch((error) => {
        toast.dismiss(loadingToast)
        setLoading(false)
        setIsLastProductDeleteModalOpen(false)
        handleApiError(error, modal)
      })
  }

  const handleDeleteOrderProduct = (product: OrderProductModel) => {
    if (orderProducts.length === 1) {
      setProductPendingDeletion(product)
      setIsLastProductDeleteModalOpen(true)
      return
    }

    executeDeleteProduct(product, false)
  }

  const handleGoBackToSourceOrder = () => {
    const sourceCode = order.orderModel?.sourceOrderShortModel?.code

    if (sourceCode) {
      navigate(`/profile/orders/${sourceCode}`)
    } else {
      navigate(-1)
    }
  }

  const isMobileView = [screen?.md, screen?.sm, screen?.xs].some((v) => v)

  const totals = getOrderTotals(
    orderProducts,
    (product) =>
      duplicateOrderProductsQuantities[String(getProductKey(product))]
  )

  const confirmCancelOrder = () => {
    atomModal.confirm({
      title: 'Отмена заказа',
      content: 'Вы уверены, что хотите отменить этот заказ?',
      okText: 'Да, отменить',
      cancelText: 'Нет',
      danger: true,
      onOk: () => handleStatusTransition('CANCELED'),
    })
  }

  const openSupplierOrder = (supplierOrderCode: number) => {
    navigate(`/profile/orders/${supplierOrderCode}`)
    setInOrderToSupplier(true)
    setOrdersToSuppliers([])
    setOrder({} as OrderInterface)
  }

  return (
    <>
      {contextHolder}
      {atomModalHolder}
      <ValidationErrorModal
        error={validationError}
        lines={toFlatOfferLines(orderProducts)}
        onClose={() => setValidationError(null)}
      />
      {Object.keys(order).length && orderPermissions ? (
        <section className={cn.order}>
          <div className={cn.inner}>
            <div className={cn.actionsRow}>
              {orderPermissions.canTakeInWork && (
                <AtomButton onClick={handleTakeInWork}>
                  Взять в работу
                </AtomButton>
              )}
              {orderPermissions.canChangeStatus &&
                orderPermissions.availableTransitions?.includes(
                  'SUBMITTED_BY_CLIENT'
                ) && (
                  <AtomButton
                    onClick={() =>
                      handleStatusTransition('SUBMITTED_BY_CLIENT')
                    }
                  >
                    Подтвердить заказ
                  </AtomButton>
                )}
              {orderPermissions.canChangeStatus &&
                orderPermissions.availableTransitions?.includes('CANCELED') && (
                  <AtomButton variant="danger" onClick={confirmCancelOrder}>
                    Отменить заказ
                  </AtomButton>
                )}
              <OrderExportButtons orderCode={Number(code)} />
              {orderPermissions.canAddProducts && (
                <AtomButton variant="secondary" onClick={handleOpenSelection}>
                  Подбор товара
                </AtomButton>
              )}
            </div>

            <OrderInfoCards
              order={order.orderModel}
              totals={totals}
              isOrganisationSelected={isOrganisationSelected}
            />
            {order.orderModel.sourceOrderShortModel ? (
              <div
                className={cn.backIconContainer}
                onClick={handleGoBackToSourceOrder}
              >
                <LeftOutlined /> Вернуться к исходному заказу
              </div>
            ) : null}
            <div className={cn.mainContent}>
              {!isInOrderToSupplier && (
                <AtomSwitch
                  options={ORDER_TAB_OPTIONS}
                  value={currentTab}
                  onChange={setCurrentTab}
                  ariaLabel="Разделы заказа"
                  isOrganisationSelected={isOrganisationSelected}
                  className={cn.tabs}
                />
              )}
              {currentTab === 'products' &&
                (isMobileView && orderProducts.length > 0 ? (
                  <div className={cn.orderProductCards}>
                    {orderProducts.map((product) => (
                      <OrderProductCard
                        key={getProductKey(product)}
                        product={product}
                        orderCode={order.orderModel.code}
                        duplicateOrderProductsQuantities={
                          duplicateOrderProductsQuantities
                        }
                        canEditProducts={orderPermissions.canEditProducts}
                        handleDeleteOrderProduct={handleDeleteOrderProduct}
                      />
                    ))}
                  </div>
                ) : (
                  <AtomTable
                    data={orderProducts}
                    columns={getProductsColumns({
                      orderCode: order.orderModel.code,
                      duplicateOrderProductsQuantities,
                      handleDeleteOrderProduct,
                      isStaff,
                      canEditProducts: orderPermissions.canEditProducts,
                    })}
                    getRowId={(product) => String(getProductKey(product))}
                    columnWidths={ORDER_PRODUCT_COLUMN_WIDTHS}
                    isOrganisationSelected={isOrganisationSelected}
                    emptyContent="Нет товаров"
                    ariaLabel="Товары заказа"
                  />
                ))}
              {!isInOrderToSupplier && currentTab === 'suppliers' && (
                <>
                  {ordersToSuppliers.length ? (
                    <>
                      {isMobileView ? (
                        <div className={cn.orderCards}>
                          {ordersToSuppliers.map((orderItem) => (
                            <OrderCard
                              key={orderItem.code}
                              order={orderItem}
                              onClick={() => openSupplierOrder(orderItem.code)}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className={cn.table}>
                          <Table
                            columns={orderToSupplierColumns}
                            dataSource={ordersToSuppliers}
                            pagination={false}
                            rowKey={'code'}
                            onRow={(record) => ({
                              onMouseUp: createSafeRowMouseUp(() =>
                                openSupplierOrder(record.code)
                              ),
                            })}
                          />
                        </div>
                      )}
                      <div
                        className={[
                          cn.summarySticky,
                          isOrganisationSelected
                            ? cn.summaryStickyBlue
                            : cn.summaryStickyOrange,
                        ].join(' ')}
                      >
                        <div className={cn.selectedCountBlock}>
                          <p>
                            Всего заказов:{' '}
                            <b>
                              {(() => {
                                const activeOrders = ordersToSuppliers.filter(
                                  (order: OrderShortType) =>
                                    order.orderStatus !== 'REJECTED'
                                )
                                return activeOrders.length
                              })()}
                            </b>
                          </p>
                        </div>
                        <div className={cn.summaryTotals}>
                          <p>
                            Итого, BYN:{' '}
                            <b>
                              {(() => {
                                let total = 0
                                ordersToSuppliers.forEach(
                                  (order: OrderShortType) => {
                                    if (
                                      order.orderStatus !== 'REJECTED' &&
                                      order.totalPrice
                                    ) {
                                      total += order.totalPrice
                                    }
                                  }
                                )
                                return trimTrailingZeros(total)
                              })()}
                            </b>
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className={cn.emptyMessage}>Нет заказов</p>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      ) : (
        <Loader loaderType="modal" isModalOpen={loading} />
      )}

      <Modal
        open={showTakeInWorkConfirmModal}
        onCancel={() => setShowTakeInWorkConfirmModal(false)}
        className={cn.modal}
        title={
          <div className={cn.title}>
            <span className={cn.titleIcon}>⚠️</span>
            <span className={cn.titleText}>Требуется действие</span>
          </div>
        }
        footer={
          <div className={cn.footer}>
            <Button
              key="manual"
              size="large"
              onClick={async () => {
                setShowTakeInWorkConfirmModal(false)
                await executeTakeInWork({ createSubOrders: false })
              }}
              className={cn.buttonCancel}
            >
              Нет
            </Button>
            <Button
              key="auto"
              type="primary"
              size="large"
              onClick={async () => {
                setShowTakeInWorkConfirmModal(false)
                await fetchTakeInWorkPreview()
              }}
              className={accentButtonClassName}
            >
              Да, перейти к созданию
            </Button>
          </div>
        }
        width={480}
        centered
        closable
        maskClosable
      >
        <div className={cn.content}>
          Этот заказ содержит товары от одного или нескольких внешних
          поставщиков. Создать для них заказы на закупку автоматически?
        </div>
      </Modal>

      <Modal
        title="Создание заказов поставщикам"
        open={subOrderModalVisible}
        onOk={handleSubOrderFormSubmit}
        onCancel={() => setSubOrderModalVisible(false)}
        okButtonProps={{
          className: `${accentButtonClassName} ${cn.accentButtonCompact}`,
        }}
        cancelText="Отмена"
        okText="Создать"
        width={800}
      >
        {previewData && previewData.subOrderRequirements ? (
          <div className={cn.subOrderForm}>
            {previewData.subOrderRequirements.length > 0 ? (
              previewData.subOrderRequirements.map((requirement) => (
                <div
                  key={requirement.supplierOwnerOrgId}
                  className={cn.supplierBlock}
                >
                  <h3>Поставщик: {requirement.supplierDescription}</h3>

                  {requirement.products && requirement.products.length > 0 && (
                    <div className={cn.supplierProducts}>
                      <div className={cn.supplierProductsTitle}>
                        Товары в заказе:
                      </div>
                      <div className={cn.supplierProductsList}>
                        {requirement.products.map((prod: OrderProductModel) => {
                          const quantity = prod.quantity ?? 0
                          const optPriceWithVAT = withVat(prod.price ?? 0)
                          const optTotal = prod.total ?? 0
                          const rrcPriceWithVAT = withVat(prod.retailPrice ?? 0)
                          const rrcTotalWithVAT = withVat(
                            prod.totalRetailPrice ?? 0
                          )

                          const unit =
                            prod.nomenclatureDto.unitOfMeasurementDto
                              ?.description || 'шт'

                          return (
                            <div
                              key={prod.nomenclatureDto.id}
                              className={cn.supplierProduct}
                            >
                              <div className={cn.supplierProductInfo}>
                                <div className={cn.supplierProductName}>
                                  {prod.nomenclatureDto.description}
                                </div>
                                <div className={cn.supplierProductMeta}>
                                  <div>
                                    Бренд:{' '}
                                    <span className={cn.supplierMetaValue}>
                                      {
                                        prod.nomenclatureDto.brandDto
                                          ?.description
                                      }
                                    </span>
                                  </div>
                                  <div>
                                    Артикул:{' '}
                                    <span className={cn.supplierMetaValue}>
                                      {prod.nomenclatureDto.article}
                                    </span>
                                  </div>
                                  {prod.storeShortModel?.description && (
                                    <div className={cn.supplierProductStore}>
                                      Склад:{' '}
                                      <span className={cn.supplierMetaValue}>
                                        {prod.storeShortModel.description}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className={cn.supplierProductTotals}>
                                <div className={cn.supplierProductQuantity}>
                                  <strong>
                                    {quantity} {unit}
                                  </strong>
                                </div>

                                <div className={cn.supplierProductPrices}>
                                  <div>
                                    <span className={cn.supplierPriceLabel}>
                                      ОПТ:{' '}
                                    </span>
                                    {trimTrailingZeros(optPriceWithVAT)} BYN
                                  </div>
                                  <div className={cn.supplierPriceTotal}>
                                    <span className={cn.supplierPriceLabel}>
                                      Сумма:{' '}
                                    </span>
                                    {trimTrailingZeros(optTotal)} BYN
                                  </div>
                                  {rrcPriceWithVAT > 0 && (
                                    <div className={cn.supplierRetailPrices}>
                                      <div>
                                        <span
                                          className={cn.supplierRetailLabel}
                                        >
                                          РРЦ цена:{' '}
                                        </span>
                                        {trimTrailingZeros(rrcPriceWithVAT)} BYN
                                      </div>
                                      <div className={cn.supplierMetaValue}>
                                        <span
                                          className={cn.supplierRetailLabel}
                                        >
                                          РРЦ сумма:{' '}
                                        </span>
                                        {trimTrailingZeros(rrcTotalWithVAT)} BYN
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {requirement.requiredInputs &&
                  requirement.requiredInputs.length > 0 ? (
                    <div className={cn.supplierInputs}>
                      {requirement.requiredInputs.map((input) => {
                        const key = `${requirement.supplierOwnerOrgId}_${input.type}`
                        return (
                          <div key={key} className={cn.formField}>
                            <label className={cn.supplierInputLabel}>
                              {getFieldLabel(input.type)}{' '}
                              <span className={cn.requiredMark}>*</span>
                            </label>
                            <Select
                              className={cn.fullWidth}
                              placeholder={`Выберите ${getFieldPlaceholder(input.type)}`}
                              value={subOrderInputs[key]?.value || undefined}
                              onChange={(value) =>
                                handleInputChange(
                                  requirement.supplierOwnerOrgId,
                                  input.type,
                                  value
                                )
                              }
                              status={!subOrderInputs[key]?.value ? '' : ''}
                            >
                              {input.options?.map((option) => (
                                <Select.Option
                                  key={option.id}
                                  value={option.id}
                                >
                                  {option.description}
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className={cn.supplierReady}>
                      ✓ Все необходимые данные для этого поставщика уже есть
                    </div>
                  )}

                  <div className={cn.supplierComment}>
                    <label className={cn.supplierCommentLabel}>
                      Комментарий к заказу (необязательно)
                    </label>
                    <Input.TextArea
                      rows={2}
                      placeholder="Добавьте комментарий для поставщика..."
                      value={
                        subOrderComments[requirement.supplierOwnerOrgId] || ''
                      }
                      onChange={(e) =>
                        setSubOrderComments((prev) => ({
                          ...prev,
                          [requirement.supplierOwnerOrgId]: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className={cn.subOrderEmpty}>
                <p className={cn.subOrderEmptyText}>
                  Нет требований для создания под-заказов
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className={cn.subOrderLoading}>
            <Loader />
            <p className={cn.subOrderLoadingText}>
              Загрузка данных предварительного просмотра...
            </p>
          </div>
        )}
      </Modal>

      <Modal
        title="Удаление товара"
        open={isLastProductDeleteModalOpen}
        centered
        onCancel={() => {
          setIsLastProductDeleteModalOpen(false)
          setProductPendingDeletion(null)
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsLastProductDeleteModalOpen(false)
              setProductPendingDeletion(null)
            }}
          >
            Отмена
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            onClick={() => {
              if (productPendingDeletion) {
                executeDeleteProduct(productPendingDeletion, true)
              }
            }}
          >
            Да, удалить
          </Button>,
        ]}
      >
        <p>После удаления последнего товара заказ будет удален.</p>
      </Modal>

      {order?.orderModel && (
        <OrderProductsSelectionModal
          open={isSelectionModalOpen}
          onClose={() => setIsSelectionModalOpen(false)}
          orderCode={Number(code)}
          onSuccess={fetchOrderData}
          existingProducts={orderProducts}
        />
      )}
    </>
  )
}

export default Order
